import { makeSkeleton, type BoneDef, type Skeleton } from '@/core/skeleton';
import { type Mesh } from '@/gfx/mesh';
import { createBoxMesh } from './box';
import { createCylinderMesh } from './cylinder';

export const CHARACTER_BONES = {
  HIP: 0,
  SPINE: 1,
  HEAD: 2,
  ARM_L: 3,
  ARM_R: 4,
  LEG_L: 5,
  LEG_R: 6,
} as const;

const BONE_DEFS: BoneDef[] = [
  { name: 'hip', parent: -1, bindPosX: 0, bindPosY: 0.8, bindPosZ: 0 },
  { name: 'spine', parent: CHARACTER_BONES.HIP, bindPosX: 0, bindPosY: 0.05, bindPosZ: 0 },
  { name: 'head', parent: CHARACTER_BONES.SPINE, bindPosX: 0, bindPosY: 0.65, bindPosZ: 0 },
  { name: 'armL', parent: CHARACTER_BONES.SPINE, bindPosX: -0.22, bindPosY: 0.55, bindPosZ: 0 },
  { name: 'armR', parent: CHARACTER_BONES.SPINE, bindPosX: 0.22, bindPosY: 0.55, bindPosZ: 0 },
  { name: 'legL', parent: CHARACTER_BONES.HIP, bindPosX: -0.1, bindPosY: 0, bindPosZ: 0 },
  { name: 'legR', parent: CHARACTER_BONES.HIP, bindPosX: 0.1, bindPosY: 0, bindPosZ: 0 },
];

const offsetMesh = (mesh: Mesh, dx: number, dy: number, dz: number): Mesh => {
  const positions = new Float32Array(mesh.positions);
  for (let i = 0; i < positions.length; i += 3) {
    positions[i] = positions[i]! + dx;
    positions[i + 1] = positions[i + 1]! + dy;
    positions[i + 2] = positions[i + 2]! + dz;
  }
  return {
    positions,
    normals: new Float32Array(mesh.normals),
    uvs: new Float32Array(mesh.uvs),
    indices:
      mesh.indices instanceof Uint16Array
        ? new Uint16Array(mesh.indices)
        : new Uint32Array(mesh.indices),
  };
};

const attachBone = (mesh: Mesh, boneIndex: number): Mesh => {
  const vertCount = mesh.positions.length / 3;
  const boneIndices = new Uint8Array(vertCount * 4);
  const boneWeights = new Float32Array(vertCount * 4);
  for (let i = 0; i < vertCount; i++) {
    boneIndices[i * 4] = boneIndex;
    boneWeights[i * 4] = 1;
  }
  return { ...mesh, boneIndices, boneWeights };
};

const concatMeshes = (meshes: ReadonlyArray<Mesh>): Mesh => {
  let totalVerts = 0;
  let totalIdx = 0;
  for (const m of meshes) {
    totalVerts += m.positions.length / 3;
    totalIdx += m.indices.length;
  }
  const positions = new Float32Array(totalVerts * 3);
  const normals = new Float32Array(totalVerts * 3);
  const uvs = new Float32Array(totalVerts * 2);
  const boneIndices = new Uint8Array(totalVerts * 4);
  const boneWeights = new Float32Array(totalVerts * 4);
  const indices = new Uint16Array(totalIdx);
  let vOff = 0;
  let iOff = 0;
  for (const m of meshes) {
    const vCount = m.positions.length / 3;
    positions.set(m.positions, vOff * 3);
    normals.set(m.normals, vOff * 3);
    uvs.set(m.uvs, vOff * 2);
    if (m.boneIndices !== undefined) boneIndices.set(m.boneIndices, vOff * 4);
    if (m.boneWeights !== undefined) boneWeights.set(m.boneWeights, vOff * 4);
    for (let i = 0; i < m.indices.length; i++) {
      indices[iOff + i] = m.indices[i]! + vOff;
    }
    vOff += vCount;
    iOff += m.indices.length;
  }
  return { positions, normals, uvs, indices, boneIndices, boneWeights };
};

export interface CharacterMeshData {
  mesh: Mesh;
  skeleton: Skeleton;
}

export const createCharacterMesh = (): CharacterMeshData => {
  const skeleton = makeSkeleton(BONE_DEFS);
  const B = CHARACTER_BONES;

  const torso = attachBone(offsetMesh(createBoxMesh(0.36, 0.6, 0.26), 0, 0.3, 0), B.SPINE);
  const head = attachBone(offsetMesh(createBoxMesh(0.3, 0.4, 0.3), 0, 0.2, 0), B.HEAD);
  const armL = attachBone(
    offsetMesh(createCylinderMesh(0.08, 0.08, 0.65, 6), 0, -0.65, 0),
    B.ARM_L,
  );
  const armR = attachBone(
    offsetMesh(createCylinderMesh(0.08, 0.08, 0.65, 6), 0, -0.65, 0),
    B.ARM_R,
  );
  const legL = attachBone(offsetMesh(createCylinderMesh(0.1, 0.1, 0.8, 6), 0, -0.8, 0), B.LEG_L);
  const legR = attachBone(offsetMesh(createCylinderMesh(0.1, 0.1, 0.8, 6), 0, -0.8, 0), B.LEG_R);

  const mesh = concatMeshes([torso, head, armL, armR, legL, legR]);
  return { mesh, skeleton };
};
